import axios, {
  AxiosHeaders,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';

import {
  CreateApiClient,
  RequestInterceptor,
  RequestOptions,
  ResponseInterceptor,
} from './index.d';

const axiosResponseToData = <T>(response: AxiosResponse<T>): T => response.data;

const defaultValidateStatus = (status: number) => status >= 200 && status < 300;

const normalizeToArray = <T extends object>(value: T | T[] | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const parseRequestConfig = <T extends Record<string, unknown>>(
  urlOrOptions: string | RequestOptions<T, T>,
  dataOrParams?: T,
  signal?: AbortSignal,
  requestValidateStatus?: (status: number) => boolean,
  dataKey: 'data' | 'params' = 'data',
  fallbackValidateStatus: (status: number) => boolean = defaultValidateStatus,
) => {
  if (typeof urlOrOptions === 'string') {
    return {
      url: urlOrOptions,
      [dataKey]: dataOrParams,
      signal,
      validateStatus: requestValidateStatus || fallbackValidateStatus,
      timeout: undefined as number | undefined,
      headers: undefined as RequestOptions['headers'],
    };
  }
  return {
    url: urlOrOptions.url,
    [dataKey]: urlOrOptions[dataKey],
    signal: urlOrOptions.signal,
    validateStatus: urlOrOptions.validateStatus || fallbackValidateStatus,
    timeout: urlOrOptions.timeout,
    headers: urlOrOptions.headers,
  };
};

const buildOptionalConfig = (reqConfig: ReturnType<typeof parseRequestConfig>) => ({
  ...(reqConfig.signal && { signal: reqConfig.signal }),
  ...(reqConfig.timeout && { timeout: reqConfig.timeout }),
  ...(reqConfig.headers && { headers: reqConfig.headers }),
});

const applyInterceptors = (
  axiosInstance: ReturnType<typeof axios.create>,
  interceptors?: {
    request?: RequestInterceptor | RequestInterceptor[];
    response?: ResponseInterceptor | ResponseInterceptor[];
  },
  injectedParams?: Record<string, unknown>,
) => {
  if (!interceptors) return;

  // Phase 1.2: normalizeToArray 일관성 적용
  normalizeToArray(interceptors.request).forEach((interceptor) => {
    axiosInstance.interceptors.request.use(
      // Phase 2.1: 널 병합 연산자로 일관된 처리
      (config: InternalAxiosRequestConfig) =>
        interceptor.onFulfilled?.(config, injectedParams) ?? config,
      interceptor.onRejected,
      interceptor.options,
    );
  });

  normalizeToArray(interceptors.response).forEach((interceptor) => {
    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => interceptor.onFulfilled?.(response, injectedParams) ?? response,
      (error) => interceptor.onRejected?.(error, injectedParams) ?? Promise.reject(error),
    );
  });
};

const createApiClient = ({
  baseURL,
  headers,
  withCredentials = true,
  config,
  onRequest,
  interceptors,
  interceptorFactory,
  validateStatus = defaultValidateStatus,
  injectedParams,
}: CreateApiClient) => {
  const request = axios.create({
    baseURL,
    headers,
    withCredentials,
    timeout: config?.timeout || 3000,
    ...config,
  });

  // Axios request interceptor는 LIFO로 실행되므로, 여기서 먼저 등록된 onRequest는
  // 아래 applyInterceptors로 등록되는 (auth 등) 인터셉터보다 나중에 실행된다.
  // 즉 인증 헤더 주입 이후에 i18n 글로벌 파라미터를 덧붙이는 순서가 의도된 설계.
  if (onRequest) {
    request.interceptors.request.use(onRequest);
  }

  let finalInterceptors = interceptors;
  if (interceptorFactory) {
    const factoryInterceptors = {
      request: interceptorFactory.request?.(injectedParams),
      response: interceptorFactory.response?.(injectedParams),
    };

    finalInterceptors = {
      request: [
        ...normalizeToArray(interceptors?.request),
        ...normalizeToArray(factoryInterceptors.request),
      ],
      response: [
        ...normalizeToArray(interceptors?.response),
        ...normalizeToArray(factoryInterceptors.response),
      ],
    };
  }

  applyInterceptors(request, finalInterceptors, injectedParams);

  // Body를 가지는 메서드 (POST, PUT, PATCH) 팩토리
  const createBodyMethod =
    (method: 'post' | 'put' | 'patch') =>
    async <U extends string, D extends Record<string, unknown>>(
      urlOrOptions: U | RequestOptions<never, D>,
      data?: D,
      signal?: AbortSignal,
      requestValidateStatus?: (status: number) => boolean,
    ) => {
      const reqConfig = parseRequestConfig(
        urlOrOptions,
        data,
        signal,
        requestValidateStatus,
        'data',
        validateStatus,
      );
      const response = await request[method](reqConfig.url, reqConfig.data, {
        validateStatus: reqConfig.validateStatus,
        ...buildOptionalConfig(reqConfig),
      });
      return axiosResponseToData(response);
    };

  // DELETE는 body를 config 객체 내부에 전달
  const requestDelete = async <U extends string, D extends Record<string, unknown>>(
    urlOrOptions: U | RequestOptions<never, D>,
    data?: D,
    signal?: AbortSignal,
    requestValidateStatus?: (status: number) => boolean,
  ) => {
    const reqConfig = parseRequestConfig(
      urlOrOptions,
      data,
      signal,
      requestValidateStatus,
      'data',
      validateStatus,
    );
    const response = await request.delete(reqConfig.url, {
      data: reqConfig.data,
      validateStatus: reqConfig.validateStatus,
      ...buildOptionalConfig(reqConfig),
    });
    return axiosResponseToData(response);
  };

  // GET은 params를 사용
  const requestGet = async <U extends string, P extends Record<string, unknown>>(
    urlOrOptions: U | RequestOptions<P>,
    params?: P,
    signal?: AbortSignal,
    requestValidateStatus?: (status: number) => boolean,
  ) => {
    const reqConfig = parseRequestConfig(
      urlOrOptions,
      params,
      signal,
      requestValidateStatus,
      'params',
      validateStatus,
    );
    const response = await request.get(reqConfig.url, {
      params: reqConfig.params,
      validateStatus: reqConfig.validateStatus,
      ...buildOptionalConfig(reqConfig),
    });
    return axiosResponseToData(response);
  };

  const requestPost = createBodyMethod('post');
  const requestPatch = createBodyMethod('patch');
  const requestPut = createBodyMethod('put');

  return {
    requestDelete,
    requestGet,
    requestPost,
    requestPut,
    requestPatch,
    _axios: request,
    _injectedParams: injectedParams,
  };
};

// retry count를 추적하기 위한 WeakMap (config 객체 mutation 방지)
const retryCountMap = new WeakMap<InternalAxiosRequestConfig, number>();

const RETRYABLE_STATUS_CODES = new Set([408, 429]);

const isRetryableError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;

  // 네트워크 에러 또는 타임아웃 → retry
  if (!error.response) return true;

  // retryable 상태 코드만 retry
  return RETRYABLE_STATUS_CODES.has(error.response.status);
};

const commonInterceptors = {
  createAuthInterceptor: (token?: string): RequestInterceptor => ({
    onFulfilled: (config) => {
      if (token) {
        config.headers = config.headers as AxiosHeaders;
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
  }),

  createGenderInterceptor: (genderProvider?: () => string): RequestInterceptor => ({
    onFulfilled: (config) => {
      const gender = genderProvider?.();
      if (gender) {
        config.params = { ...config.params, gf: gender };
      }
      return config;
    },
  }),

  createErrorLoggingInterceptor: ({
    service,
    logging,
  }: {
    service: string;
    logging?: (error: unknown, params?: Record<string, unknown>) => void;
  }): ResponseInterceptor => ({
    onRejected: (error, params) => {
      console.error(`[${service}] API Error:`, error);
      logging?.(error, params);
      throw error;
    },
  }),

  createRetryInterceptor: (maxRetries = 3): ResponseInterceptor => ({
    onRejected: async (error: unknown) => {
      // Axios 에러가 아니거나 config가 없으면 바로 throw
      if (!isAxiosError(error) || !error.config) {
        throw error;
      }

      // 4xx 클라이언트 에러 등 retryable하지 않은 에러는 즉시 throw
      if (!isRetryableError(error)) {
        throw error;
      }

      const { config } = error;

      // WeakMap에서 현재 retry count 조회
      const currentRetryCount = retryCountMap.get(config) ?? 0;

      // 최대 재시도 횟수 초과 시 throw
      if (currentRetryCount >= maxRetries) {
        throw error;
      }

      // retry count 증가
      retryCountMap.set(config, currentRetryCount + 1);

      // Exponential backoff: 1s → 2s → 4s (최대 10초)
      const delay = Math.min(1000 * 2 ** currentRetryCount, 10000);
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), delay);
      });

      // isAxiosError 타입 가드로 config는 InternalAxiosRequestConfig 타입
      return axios(config);
    },
  }),
};

export { createApiClient, commonInterceptors };
